import React, { useEffect, useRef, useState, Dispatch, SetStateAction } from 'react';
import { useParams } from 'react-router-dom';

import TableBody from '@mui/material/TableBody';
import { TextField, Tooltip, Button, Stack, TableCell, TableRow } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSnackbarContext, useOptionAddonContext, useRestaurantOptionAddonContextV2 } from '@/services/index';
import { QueryParamsGetList, RestaurantAddOnsSchema, order } from '@/types/index';
import Modal from '../modal/modalOptionAddon';
import { LIMIT_EN_CHAR, LIMIT_TH_CHAR } from '@/app.constants/index';
import ModalCancelOrder from '../modal/modalCancelOrder';
import { PriceInput } from '../sharedPriceInputComponent';
import { Addon, AddonRequest } from '@/src/types/crud/optionAddon/optionAddon';
import { OptionAddonOrderByType } from '@/src/types/crud/optionAddon/optionAddon.queries';
import { STATE_ENUM } from '@/src/types/schema/enum/common.enum';

interface EnhancedTableBodyRestaurantOptionAddonProps {
  rows: Addon[];
  order: order | null;
  orderBy: OptionAddonOrderByType | null;
  setOrderBy: React.Dispatch<React.SetStateAction<OptionAddonOrderByType | null>>;
  setOrder: React.Dispatch<React.SetStateAction<order | null>>;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setTotal: Dispatch<SetStateAction<number>>;
  setIsChange: Dispatch<SetStateAction<boolean>>;
  total: number;
  setPaginationParams: React.Dispatch<React.SetStateAction<QueryParamsGetList<any>>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
}

export function EnhancedTableBodyOptionAddon(props: EnhancedTableBodyRestaurantOptionAddonProps) {
  const { rows, order, orderBy, setOrderBy, setOrder, setOpen, setTotal, setIsChange, total, setPaginationParams, setPage } = props;
  const [editRowIndex, setEditRowIndex] = React.useState<undefined | number>(-1);

  const [type, setType] = useState('');
  const [openModal, setOpenModal] = useState(false);

  const [thaiText, setThaiText] = React.useState('');
  const [engText, setEngText] = React.useState('');
  const [priceText, setPrice] = React.useState('');

  const [prevAddon, setPrevAddon] = React.useState({
    prevThai: '',
    prevEng: '',
    prevPrice: '',
  });

  const [thNameErrorText, setThNameErrorText] = React.useState('');
  const [enNameErrorText, setEnNameErrorText] = React.useState('');
  const [priceErrorText, setPriceErrorText] = React.useState('');

  const countThaiText = useRef(0);
  const countEngText = useRef(0);
  const countPrice = useRef(0);

  const [validate, setValidate] = React.useState(false);
  const thaiChar = LIMIT_TH_CHAR;
  const engChar = LIMIT_EN_CHAR;
  const [checkDup, setCheckDup] = useState(false);
  const [checkLoad, setCheckLoad] = useState(false);
  const { id, groupId } = useParams();

  const focusTextField = useRef<HTMLInputElement | null>(null);
  const loopTextField = useRef<HTMLInputElement | null>(null);

  // use context
  const { setResponseStatus } = useSnackbarContext();
  const { restaurantOptionAddonList } = useOptionAddonContext();

  const { optionAddonList, optionAddonUpdate, optionAddonDelete, getOptionAddonUpdateList, getOptionAddonDeleteList, overBudgetOrdersByAddonOption } =
    useRestaurantOptionAddonContextV2();

  const [optionAddonsId, setOptionAddonsId] = useState('');
  // state for modal confirmation when the new price affects orders to go over budget.
  const [openCancelOrdersModal, setOpenCancelOrdersModal] = useState<boolean>(false);
  // state for the waiting response from over-budget check, use to prevent more action while checking.
  const [isCheckingOverBudget, setCheckingOverBudget] = useState<boolean>(false);
  // option add on id that currently check price and get over budget orders.
  const [currentCheckingOptionAddOnId, setCurrentCheckingOptionAddOnId] = useState<string | undefined>();
  // number of overBudget Orders that response from API.
  const [overBudgetOrdersCount, setOverBudgetOrdersCount] = useState<number>(0);

  // handle click function
  const handleOpenDelete = (optionAddonId: string) => {
    setType('DELETE');
    setOpenModal(true);
    setOptionAddonsId(optionAddonId);
  };

  const handleCancel = () => {
    setEditRowIndex(-1);
    setThNameErrorText('');
    setEnNameErrorText('');
    setPriceErrorText('');
    /** Set state to INITIAL after cancel edit */
    optionAddonUpdate.setState({ state: STATE_ENUM.INITIAL });
  };

  function handleEditClick(index: number, thName: string, enName: string, price: string) {
    setThNameErrorText('');
    setEnNameErrorText('');
    setPriceErrorText('');
    setThaiText(thName);
    setEngText(enName);
    setPrice(price);
    setPrevAddon({
      prevThai: thName,
      prevEng: enName,
      prevPrice: price,
    });
    setEditRowIndex(index);
    countEngText.current = enName.trim().length;
    countThaiText.current = thName.trim().length;
    countPrice.current = price.trim().length;
    /** Set state edit when user click edit button */
    optionAddonUpdate.setState({ state: STATE_ENUM.EDITING });
  }

  const handleCheckPriceBeforeSave = (optionAddonId: string) => {
    if (id && groupId) {
      setCheckingOverBudget(true);
      setCheckLoad(true);
      setCurrentCheckingOptionAddOnId(optionAddonId);
      overBudgetOrdersByAddonOption.requestAct(id, groupId, optionAddonId, { price: parseInt(priceText) });
    }
  };

  const handleClickSave = (optionAddonId: string) => {
    const payload = {
      thName: thaiText?.trim(),
      enName: engText?.trim(),
      price: parseInt(priceText),
    } as AddonRequest;
    setOptionAddonsId(optionAddonId);
    if (id && groupId) optionAddonUpdate.requestAct(id, groupId, optionAddonId, payload);
    setValidate(false);
  };

  useEffect(() => {
    if (overBudgetOrdersByAddonOption.current.state === STATE_ENUM.READY && isCheckingOverBudget && currentCheckingOptionAddOnId) {
      // NOTE: if get the response success then check if there is the overBudget Orders or not.
      if (overBudgetOrdersByAddonOption.current.data?.amount === 0) {
        handleClickSave(currentCheckingOptionAddOnId);
        setCurrentCheckingOptionAddOnId(undefined);
      } else {
        if (overBudgetOrdersByAddonOption.current.data?.amount) setOverBudgetOrdersCount(overBudgetOrdersByAddonOption.current.data.amount);
        setOpenCancelOrdersModal(true);
      }
      setCheckingOverBudget(false);
    }
  }, [overBudgetOrdersByAddonOption]);

  // useEffect
  useEffect(() => {
    if (optionAddonUpdate.current.state === STATE_ENUM.READY) {
      setResponseStatus({
        status: 'success',
        message: optionAddonUpdate.current?.status?.message ?? '',
      });

      optionAddonUpdate.setState({ state: STATE_ENUM.INITIAL });

      setOpen(true);
      setEditRowIndex(-1);
      setOrderBy(orderBy);
      setOrder(order);
      setCheckLoad(false);
      getOptionAddonUpdateList(optionAddonsId, thaiText?.trim(), engText?.trim(), parseInt(priceText));
    } else if (optionAddonUpdate.current.state === 'error') {
      setResponseStatus({
        status: optionAddonUpdate.current.state,
        message: optionAddonUpdate.current?.status?.message ?? '',
      });
      optionAddonUpdate.setState({ state: STATE_ENUM.INITIAL });
      setCheckLoad(false);
      setOpen(true);
    }
  }, [optionAddonUpdate.current]);

  useEffect(() => {
    if (optionAddonDelete.current.state === STATE_ENUM.READY) {
      setIsChange(true);
      setTotal(total - 1);
      if (optionAddonList.current.data?.addon.length === 1 && optionAddonList.current.pagination?.page! > 0) {
        setPage(optionAddonList.current.pagination?.page! - 1);
        setPaginationParams((prevState) => {
          return { ...prevState, page: optionAddonList.current.pagination?.page! - 1 };
        });
      } else {
        setPage(optionAddonList.current.pagination?.page!);
        setPaginationParams((prevState) => {
          return { ...prevState, page: optionAddonList.current.pagination?.page! };
        });
      }
      if (optionAddonList.current.pagination?.total !== undefined) {
        optionAddonList.current.pagination.total--;
      }

      setResponseStatus({
        status: 'success',
        message: optionAddonDelete.current?.status?.message ?? '',
      });

      optionAddonDelete.setState({ state: STATE_ENUM.INITIAL });

      setOpenModal(false);
      setOpen(true);
      setOrderBy(orderBy);
      setOrder(order);
      getOptionAddonDeleteList(optionAddonsId);
    }
  }, [optionAddonDelete.current]);

  // function check Duplicate

  const checkDuplicateThai = () => {
    const tempArray = restaurantOptionAddonList.data.addon;
    return tempArray.find((item: RestaurantAddOnsSchema) => item.thName === thaiText?.trim() && prevAddon.prevThai !== thaiText?.trim());
  };

  const checkDuplicateEng = () => {
    const tempArray = restaurantOptionAddonList.data.addon;
    return tempArray.find(
      (item: RestaurantAddOnsSchema) => item.enName.toUpperCase() === engText?.trim().toUpperCase() && prevAddon.prevEng !== engText?.trim()
    );
  };

  // function handle change
  const handleThaiNameTextChange = (event: any, index: number) => {
    countThaiText.current = event.target.value.trim().length;
    setEditRowIndex(index);
    setThaiText(event.target.value);
    if (event.target.value.length === 0 || !event.target.value.match(thaiChar)) {
      setThNameErrorText('Thai name is required.');
      setValidate(false);
    } else if (event.target.value.trim().length === 0) {
      setValidate(false);
      setThNameErrorText('Thai name is required.');
    } else if (event.target.value.length > 100) {
      setValidate(false);
      setThNameErrorText('Max 100 characters.');
    } else if (event.target.value.trim() === checkDuplicateThai()?.thName) {
      setThNameErrorText('This choice already exists in this restaurant.');
    } else {
      if (enNameErrorText === '' && priceErrorText === '') {
        setValidate(true);
      }
      setThNameErrorText('');
    }
  };

  const handleEngNameTextChange = (event: any, index: number) => {
    countEngText.current = event.target.value.trim().length;
    setEditRowIndex(index);
    setEngText(event.target.value);
    if (event.target.value.length === 0 || !event.target.value.match(engChar)) {
      setEnNameErrorText('English name is required.');
      setValidate(false);
    } else if (event.target.value.trim().length === 0) {
      setValidate(false);
      setEnNameErrorText('English name is required.');
    } else if (event.target.value.length > 100) {
      setValidate(false);
      setEnNameErrorText('Max 100 characters.');
    } else if (event.target.value.trim() === checkDuplicateEng()?.enName) {
      setEnNameErrorText('This choice already exists in this restaurant.');
    } else {
      if (thNameErrorText === '' && priceErrorText === '') {
        setValidate(true);
      }
      setEnNameErrorText('');
    }
  };

  // another handle function
  const handleOutOfFocusThaiName = () => {
    if (countThaiText.current === 0) {
      setThaiText('');
    }
  };
  const handleOutOfFocusEngName = () => {
    if (countEngText.current === 0) {
      setEngText('');
    }
  };

  useEffect(() => {
    if (prevAddon.prevThai === thaiText && prevAddon.prevEng === engText && prevAddon.prevPrice === priceText) {
      setCheckDup(false);
    } else if (checkDuplicateThai() === undefined && checkDuplicateEng() === undefined) {
      setCheckDup(true);
    } else {
      if (thaiText.trim() === checkDuplicateThai()?.thName) {
        setThNameErrorText('This choice already exists in this option group.');
        setCheckDup(false);
      }
      if (engText?.trim().toUpperCase() === checkDuplicateEng()?.enName.toUpperCase()) {
        setEnNameErrorText('This choice already exists in this option group.');
        setCheckDup(false);
        if (checkDuplicateEng()?.enName === prevAddon.prevEng) {
          setEnNameErrorText('');
          setCheckDup(true);
        }
      }
    }
    if (
      countEngText.current !== 0 &&
      countThaiText.current !== 0 &&
      countPrice.current !== 0 &&
      thNameErrorText === '' &&
      enNameErrorText === '' &&
      priceErrorText === ''
    ) {
      setValidate(true);
    } else {
      setValidate(false);
    }
  }, [thaiText, engText, priceText]);

  return (
    <TableBody id="tbody-option-addon">
      {rows.map((row: Addon, index: number) => {
        return (
          <TableRow
            id={'tr-option-addon-' + index}
            hover
            tabIndex={index}
            key={`${row.thName}+ ${index}`}
            sx={{ width: 20 }}
            className={`editTableData ${editRowIndex === index ? 'active' : null}`}
          >
            <TableCell id={'td-option-addon-col1-' + index} align="left" className="editInput">
              <TextField
                id={'input-option-addon-thName'}
                className="w-full"
                value={editRowIndex === index ? thaiText : row.thName}
                error={editRowIndex === index && thNameErrorText !== ''}
                onChange={(e) => {
                  handleThaiNameTextChange(e, index);
                }}
                onBlur={(e) => {
                  handleOutOfFocusThaiName();
                }}
                helperText={editRowIndex === index && thNameErrorText !== '' ? thNameErrorText : ''}
                tabIndex={-1}
                inputRef={editRowIndex === index ? focusTextField : null}
                ref={editRowIndex === index ? loopTextField : null}
                inputProps={{ maxLength: 101 }}
                disabled={checkLoad}
              />
            </TableCell>
            <TableCell id={'td-option-addon-col2-' + index} align="left" className="editInput">
              <TextField
                id={'input-option-addon-enName'}
                className="w-full"
                value={editRowIndex === index ? engText : row.enName}
                error={editRowIndex === index && enNameErrorText !== ''}
                onChange={(e) => {
                  handleEngNameTextChange(e, index);
                }}
                onBlur={(e) => {
                  handleOutOfFocusEngName();
                }}
                helperText={editRowIndex === index && enNameErrorText !== '' ? enNameErrorText : ''}
                inputProps={{ maxLength: 101 }}
                disabled={checkLoad}
              />
            </TableCell>
            <TableCell id={'td-option-addon-col3-' + index} align="left" className="editInput">
              <PriceInput
                price={priceText}
                priceErrorText={priceErrorText}
                setPrice={setPrice}
                setPriceErrorText={setPriceErrorText}
                setValidate={setValidate}
                countPrice={countPrice}
                editRowSetup={{
                  rowIndex: index,
                  rowPrice: row.price,
                  editRowIndex,
                  setEditRowIndex,
                }}
                disabled={checkLoad}
              />
            </TableCell>
            <TableCell id={'td-option-addon-col4-' + index} align="center" className="btnActionArea">
              <Stack direction="row" spacing={2} className="btnArea ">
                <Button
                  id="btn-save-option-addon"
                  variant="contained"
                  color="primary"
                  size="small"
                  className="w-20"
                  disabled={!validate || !checkDup || isCheckingOverBudget || checkLoad}
                  onClick={() => handleCheckPriceBeforeSave(row.id)}
                >
                  Save
                </Button>
                <Button
                  id="btn-cancel-option-addon"
                  variant="outlined"
                  color="primary"
                  size="small"
                  className="w-20"
                  onClick={() => handleCancel()}
                  onKeyDown={(e: any) => {
                    if (e.key === 'Tab') {
                      loopTextField.current?.focus();
                    }
                  }}
                  disabled={isCheckingOverBudget || checkLoad}
                >
                  Cancel
                </Button>
              </Stack>
              <Stack direction="row-reverse" spacing={2} className="btnAreaAction">
                <Tooltip title="Delete" placement="bottom-start">
                  <Button
                    id="btn-delete-option-addon"
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => handleOpenDelete(row.id)}
                    disabled={isCheckingOverBudget || checkLoad}
                  >
                    <DeleteIcon></DeleteIcon>
                  </Button>
                </Tooltip>
                <Tooltip title="Edit info" placement="bottom-start">
                  <Button
                    id="btn-edit-option-addon"
                    variant="outlined"
                    color="primary"
                    size="small"
                    className="whenHoverPointer"
                    onClick={() => handleEditClick(index, row.thName, row.enName, row.price.toString())}
                    disabled={isCheckingOverBudget || checkLoad}
                  >
                    <EditIcon></EditIcon>
                  </Button>
                </Tooltip>
              </Stack>
            </TableCell>
          </TableRow>
        );
      })}

      {/* NOTE: this TableRow is hidden, just for allow table to place modal inside */}
      <TableRow id="tr-option-addon-modal">
        <TableCell id="td-option-addon-modal" sx={{ padding: '0px', border: 'none' }}>
          <Modal
            id={'modal-option-addon'}
            openModal={openModal}
            type={type}
            setOpenModal={setOpenModal}
            restaurantId={id || ''}
            groupId={groupId || ''}
            optionAddonId={optionAddonsId}
            setType={setType}
            optionAddonDelete={optionAddonDelete}
          ></Modal>

          <ModalCancelOrder
            open={openCancelOrdersModal}
            overBudgetOrdersCount={overBudgetOrdersCount}
            onConfirm={() => {
              if (currentCheckingOptionAddOnId) handleClickSave(currentCheckingOptionAddOnId);
              setCurrentCheckingOptionAddOnId(undefined);
              setOverBudgetOrdersCount(0);
              setOpenCancelOrdersModal(false);
            }}
            onCancel={() => {
              setOverBudgetOrdersCount(0);
              setOpenCancelOrdersModal(false);
              setCheckLoad(false);
            }}
          />
        </TableCell>
      </TableRow>
    </TableBody>
  );
}
